/**
 * Inline SVG only — a strict CSP and an offline-ish first paint both rule out fetching
 * assets, and the launcher must be right on the first frame.
 */

/*
 * The GVFB heart, traced from the artwork the food bank supplied (`blue yellow heart.png`
 * and `white yellow heart.png`).
 *
 * Two filled paths rather than one path filled and stroked. An offset stroke cannot
 * reproduce this outline: the ring is not a uniform offset of the gold heart — it is its own
 * shape, a little heavier at the point than across the lobes — and the notch between the
 * lobes comes to a sharp V that a rounded join cannot make. Earlier hand-drawn attempts got
 * the notch about twice as deep as the artwork (25% of the height against 14%) and the
 * proportions too narrow (1.02 wide-to-tall against 1.10).
 *
 * Traced at a tolerance that keeps them near 330 characters each, which is a fraction of a
 * kilobyte once gzipped and a great deal smaller than embedding the PNGs.
 */
const HEART_OUTLINE =
  'M10.62 20.62C6.3 16.99 2.85 13.54 1.47 11.48C-1.33 7.29 0.02 1.81 4.22 0.31C4.91 0.07 5.05 0.05 6.13 0.04C8.21 0.04 9.79 0.72 11.38 2.3L12 2.93 L12.63 2.3C15.08 -0.18 18.58 -0.72 21.14 0.98C24.23 3.04 24.92 7.83 22.62 11.33C21.44 13.14 18.79 15.9 15.32 18.94C13.93 20.17 12.04 21.76 11.99 21.75C11.97 21.75 11.35 21.24 10.62 20.62Z'

const HEART_FILL =
  'M10.4 16.69C6.4 13.19 3.88 10.35 3.33 8.71C2.04 4.85 4.97 1.74 8.32 3.42C9.15 3.83 9.66 4.32 10.82 5.8C11.44 6.59 11.98 7.24 12 7.24C12.02 7.24 12.39 6.79 12.82 6.24C14.08 4.61 14.46 4.19 15.08 3.77C18 1.78 21.17 3.64 20.95 7.21C20.84 9.04 20.12 10.28 17.66 12.84C16.4 14.15 12.18 18.04 12 18.04C11.98 18.04 11.26 17.43 10.4 16.69Z'

/**
 * The inner heart is not flat gold in the artwork — it lifts to a brighter gold in the
 * middle and falls away to orange at the edge. The edge stop is derived from the configured
 * gold rather than hard-coded, so the colour stays one setting: the ratios below are the
 * ones measured between the artwork's own centre (#ECBA1C) and edge (#E18B24).
 *
 * The gradient holds the full gold out to half way before it starts falling, which is where
 * the artwork's does — sampled across its middle, the gold is flat from a quarter to three
 * quarters of the width. Starting the falloff at the centre instead turns most of the heart
 * orange with a small bright spot in it, which is not the same mark.
 */
function goldEdge(gold: string): string {
  const hex = gold.replace('#', '')

  if (!/^[0-9a-f]{6}$/i.test(hex)) {
    return gold
  }

  const shift = [0.953, 0.747, 1.286]
  const channel = (index: number): string => {
    const value = parseInt(hex.slice(index * 2, index * 2 + 2), 16) * shift[index]

    return Math.min(255, Math.round(value)).toString(16).padStart(2, '0')
  }

  return `#${channel(0)}${channel(1)}${channel(2)}`
}

/**
 * Gradients are referenced by id, and both the launcher and the panel header render a heart
 * into the same shadow root — so duplicate ids would make the second heart resolve to the
 * first one's gradient.
 */
let heartCount = 0

/**
 * The heart always keeps its outline, and the outline is what changes with the background:
 * navy on a light background, white on a dark one. That mirrors the artwork, which was
 * supplied as exactly those two versions of one heart.
 */
export function heart(outline: string, fill: string): string {
  const id = `gvfb-heart-${++heartCount}`

  return `
<svg class="heart" viewBox="-0.3 -0.3 24.6 22.36" aria-hidden="true" focusable="false">
  <radialGradient id="${id}" cx="0.5" cy="0.52" r="0.52">
    <stop offset="0" stop-color="${fill}"/>
    <stop offset="0.5" stop-color="${fill}"/>
    <stop offset="1" stop-color="${goldEdge(fill)}"/>
  </radialGradient>
  <path d="${HEART_OUTLINE}" fill="${outline}"/>
  <path d="${HEART_FILL}" fill="url(#${id})"/>
</svg>`
}

/**
 * The speech bubble's tail, for the corner of the launcher nearest the screen edge.
 *
 * The bubble itself is a bordered element rather than artwork, so it can grow with whatever
 * the launcher text is set to. Only the tail has to be drawn, and it has to merge with that
 * border: the white bar covers the border across the tail's mouth, and the path is left open
 * there so it strokes the two sides of the tail and nothing across the top. The path starts
 * at y=2, the middle of the 4px border, so the tail's stroke overlays the border band
 * exactly instead of sitting above it.
 *
 * Proportions follow the artwork, where the mouth is wider than the tail is deep. `flip`
 * mirrors it for a launcher pinned to the left.
 */
export function bubbleTail(outline: string, background: string, flip: boolean): string {
  return `
<svg class="tail" viewBox="0 0 32 20" aria-hidden="true" focusable="false"
     ${flip ? 'style="transform: scaleX(-1);"' : ''}>
  <rect x="2" y="0" width="25" height="4.5" fill="${background}"/>
  <path d="M2 2C3 8 8 14 22 18 25 12 27 8 27 2"
        fill="${background}" stroke="${outline}" stroke-width="4"
        stroke-linejoin="round" stroke-linecap="round"/>
</svg>`
}

export const CLOSE_ICON = `
<svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" focusable="false">
  <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" stroke-width="2.2"
        stroke-linecap="round" fill="none"/>
</svg>`

export const SEND_ICON = `
<svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" focusable="false">
  <path d="M2 21 23 12 2 3v7l15 2-15 2v7Z" fill="currentColor"/>
</svg>`

export const RESTART_ICON = `
<svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" focusable="false">
  <path d="M4 12a8 8 0 1 1 2.6 5.9M4 12V6m0 6h6" stroke="currentColor" stroke-width="2.1"
        stroke-linecap="round" stroke-linejoin="round" fill="none"/>
</svg>`
